import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Facebook, Instagram, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n/LanguageContext";
import { Reveal, Overline } from "../components/Reveal";
import { PageIntro } from "../components/PageIntro";
import { submitBooking } from "../lib/api";
import { EMBEDS } from "../i18n/content";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { name: "", email: "", event_type: "", event_date: "", location: "", message: "", website: "" };

export default function Contact() {
  const { t } = useLang();
  const form = t("contact.form");
  const errs = t("contact.errors");
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (values.name.trim().length < 2) e.name = errs.name;
    if (!EMAIL_RE.test(values.email)) e.email = errs.email;
    if (!values.event_type) e.event_type = errs.eventType;
    if (values.location.trim().length < 2) e.location = errs.location;
    if (values.message.trim().length < 5) e.message = errs.message;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      await submitBooking(values);
      setStatus("success");
      setValues(EMPTY);
    } catch (err) {
      setStatus("idle");
      const msg = err?.response?.status === 429 ? errs.rate : errs.generic;
      toast.error(msg);
    }
  };

  const fieldBase =
    "w-full rounded-lg bg-muted px-4 py-3.5 text-charcoal placeholder-charcoal/35 outline-none ring-terracotta/50 transition-shadow duration-200 focus:ring-2";

  return (
    <>
      <PageIntro overline={t("contact.overline")} title={t("contact.title")} body={t("contact.body")} testId="contact-intro" />

      <section className="bg-cream pb-28 lg:pb-36" data-testid="contact-body">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-[1.4fr_1fr] lg:gap-24 lg:px-10">
          <Reveal>
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-start rounded-2xl border border-border bg-sand/40 p-10 lg:p-14"
                  data-testid="contact-success"
                  role="status"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-terracotta text-white">
                    <Check className="h-7 w-7" />
                  </span>
                  <h2 className="mt-6 font-serif text-4xl text-charcoal">{t("contact.success.title")}</h2>
                  <p className="mt-3 max-w-md text-lg text-charcoal/70">{t("contact.success.body")}</p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    data-testid="contact-reset"
                    className="mt-8 rounded-full border border-border px-6 py-3 text-sm font-semibold text-charcoal transition-colors hover:border-terracotta hover:text-terracotta"
                  >
                    {t("cta.getInTouch")}
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onSubmit={onSubmit}
                  noValidate
                  className="space-y-6"
                  data-testid="booking-form"
                >
                  {/* Honeypot */}
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={values.website}
                    onChange={set("website")}
                    name="website"
                    className="absolute left-[-9999px] h-0 w-0 opacity-0"
                    data-testid="hp-website"
                  />

                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label={form.name} error={errors.name}>
                      <input data-testid="input-name" className={fieldBase} value={values.name} onChange={set("name")} placeholder={form.name} />
                    </Field>
                    <Field label={form.email} error={errors.email}>
                      <input data-testid="input-email" type="email" className={fieldBase} value={values.email} onChange={set("email")} placeholder="you@email.com" />
                    </Field>
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label={form.eventType} error={errors.event_type}>
                      <select data-testid="input-event-type" className={`${fieldBase} appearance-none`} value={values.event_type} onChange={set("event_type")}>
                        <option value="">{form.eventTypePlaceholder}</option>
                        {form.eventTypes.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label={form.date}>
                      <input data-testid="input-date" type="date" className={fieldBase} value={values.event_date} onChange={set("event_date")} />
                    </Field>
                  </div>

                  <Field label={form.location} error={errors.location}>
                    <input data-testid="input-location" className={fieldBase} value={values.location} onChange={set("location")} placeholder={form.location} />
                  </Field>

                  <Field label={form.message} error={errors.message}>
                    <textarea data-testid="input-message" rows={5} className={`${fieldBase} resize-none`} value={values.message} onChange={set("message")} placeholder={form.messagePlaceholder} />
                  </Field>

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="submit-booking"
                    className="w-full rounded-full bg-terracotta px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-terracotta-deep disabled:opacity-60 sm:w-auto"
                  >
                    {status === "sending" ? form.sending : form.submit}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </Reveal>

          <Reveal delay={0.12} className="lg:pt-4">
            <Overline>{t("contact.directTitle")}</Overline>
            <a href="mailto:info@francescadesalvia.com" data-testid="contact-email" className="mt-4 flex items-center gap-3 text-xl text-charcoal transition-colors hover:text-terracotta">
              <Mail className="h-5 w-5 text-terracotta" />
              info@francescadesalvia.com
            </a>
            <a
              href="https://wa.me/34694461956"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="mt-4 flex items-center gap-3 text-xl text-charcoal transition-colors hover:text-terracotta"
            >
              <MessageCircle className="h-5 w-5 text-terracotta" />
              WhatsApp · +34 694 46 19 56
            </a>
            <a
              href={EMBEDS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-facebook"
              className="mt-4 flex items-center gap-3 text-xl text-charcoal transition-colors hover:text-terracotta"
            >
              <Facebook className="h-5 w-5 text-terracotta" />
              Facebook · Shakti Love DEEJAY
            </a>
            <a
              href={EMBEDS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram"
              className="mt-4 flex items-center gap-3 text-xl text-charcoal transition-colors hover:text-terracotta"
            >
              <Instagram className="h-5 w-5 text-terracotta" />
              Instagram · @shakti_love_music
            </a>
            <div className="mt-10 h-px w-full bg-border" />
            <p className="mt-10 font-serif text-3xl leading-snug text-charcoal/80">
              “{t("intro.title")}”
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-semibold uppercase tracking-widest text-charcoal/50">{label}</span>
      {children}
      {error && <span className="mt-1.5 block text-sm text-destructive" role="alert">{error}</span>}
    </label>
  );
}
